// 2.3 / 3.2 — fill receipts.
//
// One row per field the turn filled: label left, display-formatted value right.
// Receipts are the only fill signal (design D4) — they are rendered from the
// response payload stored on the message, never inferred.
//
// In suggested mode a receipt whose field is still pending carries the AI-review
// treatment plus its own Confirm / Undo. Once confirmed or undone the row
// reverts to a plain receipt, so the history of what the turn proposed survives
// while the actionable state does not.

import { FillReceipt } from '../Shared/API/ApiTypes';
import styles from './ConversationalForms.module.css';
import { useConversationalForms } from './FormsContext';

type FillReceiptsProps = {
    fills: FillReceipt[];
    /** Receipts on the user's own (accent-filled) card need the inverse palette. */
    onUserCard?: boolean;
};

const FillReceipts = ({ fills, onUserCard = false }: FillReceiptsProps) => {
    const { state, confirmSuggestion, undoSuggestion } = useConversationalForms();

    if (!fills?.length) return null;

    return (
        <ul className={styles.receipts} aria-label="Fields filled from your answer">
            {fills.map((fill) => {
                const pending = state.suggested[fill.fieldId] !== undefined;
                const className = [
                    styles.receipt,
                    onUserCard ? styles.receiptOnUser : '',
                    pending ? styles.receiptPending : '',
                ]
                    .filter(Boolean)
                    .join(' ');

                return (
                    <li className={className} key={fill.fieldId} data-testid="cf-receipt">
                        <span className={styles.receiptLabel}>{fill.label}</span>
                        {fill.sensitive ? (
                            // Platform guardrail: a sensitive field's value is never
                            // echoed into the conversation history.
                            <span className={styles.receiptWithheld}>saved — not shown here</span>
                        ) : (
                            <span className={styles.receiptValue}>{fill.display}</span>
                        )}
                        {pending && (
                            <span className={styles.receiptActions}>
                                <button
                                    type="button"
                                    className={`${styles.receiptAction} ${styles.receiptActionPrimary}`}
                                    onClick={() => confirmSuggestion(fill.fieldId)}
                                >
                                    Confirm
                                </button>
                                <button
                                    type="button"
                                    className={styles.receiptAction}
                                    onClick={() => undoSuggestion(fill.fieldId)}
                                >
                                    Undo
                                </button>
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default FillReceipts;
